"use client";

import { ShieldCheck, Clock, Star, Sparkles } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Link } from "@/components/common/Link";
export default function Experience() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: gridRef,
    isVisible: gridVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const values = [{
    icon: ShieldCheck,
    title: "Absolute Discretion",
    description: "Your privacy is our mandate. Every journey is conducted with total confidentiality, ensuring your schedule and presence remain strictly private.",
    specs: ["Non-disclosure agreements standard", "Secure, private communication channels", "Professional, vetted chauffeurs"],
    image: "http://bemaxlimo.com/wp-content/uploads/2026/07/IMG_3092-2.webp?v=9c6zba2w4fd",
    mascot: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722013/img/chauffeur_emblem_silver.png",
    mascotAlt: "BEMAX LIMO emblem"
  }, {
    icon: Clock,
    title: "Precision Punctuality",
    description: "In the world of high-stakes travel, time is the ultimate currency. We operate on a schedule defined by seconds, not minutes.",
    specs: ["Real-time flight tracking integration", "Pre-arrival staging protocols", "Optimized route planning"],
    image: "http://bemaxlimo.com/wp-content/uploads/2026/07/IMG_4027.webp?v=m3zfdf9rbfq",
    mascot: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722013/img/chauffeur_emblem_silver.png",
    mascotAlt: "BEMAX LIMO emblem"
  }, {
    icon: Star,
    title: "Refined Comfort",
    description: "Our fleet is curated for the discerning traveler, offering a sanctuary of calm and luxury between your most important engagements.",
    specs: ["Climate-controlled cabin environments", "Premium leather appointments", "On-board connectivity and amenities"],
    image: "http://bemaxlimo.com/wp-content/uploads/2026/09/IMG_4853.webp?v=c5ykj5sxy48",
    mascot: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722013/img/chauffeur_emblem_silver.png",
    mascotAlt: "BEMAX LIMO emblem"
  }];
  return <section data-section-id="1072" id="the-chauffeur-experience" className="bg-background text-foreground relative overflow-hidden py-28 md:py-36 px-4 sm:px-6 lg:px-8">
      {}
      <svg aria-hidden="true" viewBox="0 0 1440 24" preserveAspectRatio="none" className="absolute top-0 left-0 w-full h-6 text-accent/30">
        <path d="M0,12 C80,4 160,20 240,10 C320,2 400,18 480,12 C560,6 640,22 720,14 C800,6 880,20 960,10 C1040,2 1120,18 1200,12 C1280,6 1360,22 1440,14 L1440,0 L0,0 Z" fill="currentColor" />
      </svg>

      {}
      <div aria-hidden="true" className="pointer-events-none absolute -top-32 -left-24 w-[480px] h-[480px] rounded-lg opacity-10 blur-3xl" style={{
      background: "var(--silver-leaf)"
    }} />
      <div aria-hidden="true" className="pointer-events-none absolute -bottom-40 -right-24 w-[560px] h-[560px] rounded-lg opacity-10 blur-3xl" style={{
      background: "var(--mercury-accent)"
    }} />

      <div className="relative max-w-[1320px] mx-auto">
        {}
        <div ref={headerRef} className={`grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-end mb-16 md:mb-24 transition-all duration-700 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <div className="lg:col-span-7">
            <div className="flex items-center gap-2 mb-6">
              <Sparkles className="w-4 h-4 text-silver-leaf" strokeWidth={1.75} />
              <span className="font-mono uppercase text-silver-leaf" style={{
              fontSize: "clamp(0.7rem, 0.8vw, 0.8125rem)",
              letterSpacing: "0.18em"
            }}>
                § 01 // EXPERIENCE
              </span>
            </div>
            <h2 className="font-default text-foreground" style={{
            fontSize: "clamp(2rem, 4.2vw, 3.75rem)",
            lineHeight: 1.05,
            letterSpacing: "-0.02em",
            fontWeight: 500
          }}>BEMAX <span className="italic text-silver-leaf inline-block" contenteditable="false" style={{
              transform: 'rotate(-1.5deg) translateY(2px)'
            }}></span>CHAUFFEUR</h2>
          </div>
          <div className="lg:col-span-5 flex flex-col justify-between">
            <p className="font-default text-muted-foreground mb-6" style={{
            fontSize: "clamp(1.0625rem, 1.15vw, 1.1875rem)",
            lineHeight: 1.65
          }}>
              We deliver a seamless, high-contrast environment designed for elite travel. Every journey is executed with absolute precision and discretion.
            </p>
            <div className="flex flex-wrap items-center gap-3 pt-2 font-mono text-xs uppercase tracking-wider text-muted-foreground">
              <span className="text-foreground font-medium">Explore:</span>
              <Link to="/FleetPage" className="text-silver-leaf hover:underline transition-all">
                /Fleet
              </Link>
              <span className="text-border">/</span>
              <Link to="/SecureChauffeurPage" className="text-silver-leaf hover:underline transition-all">
                /SecureChauffeur
              </Link>
              <span className="text-border">/</span>
              <Link to="/AboutUsPage" className="text-silver-leaf hover:underline transition-all">
                /AboutUs
              </Link>
            </div>
          </div>
        </div>

        {}
        <div ref={gridRef} className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 transition-all duration-700 ease-out ${gridVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          {values.map((value, i) => {
          const Icon = value.icon;
          return <Card data-index={i} key={value.title} className="group relative bg-card border border-border hover:border-silver-leaf transition-all duration-300 ease-out hover:-translate-y-1 rounded-lg overflow-visible" style={{
            padding: "36px 28px"
          }}>
                <CardContent className="px-0 flex flex-col items-start">
                  {}
                  <div className="mb-7 mx-auto md:mx-0 w-[120px] h-[120px] rounded-lg bg-secondary flex items-center justify-center overflow-hidden border border-border">
                    <img src={value.image} alt={`${value.title} illustration`} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-300 ease-out group-hover:scale-[1.03]" />
                  </div>

                  {}
                  <h3 className="font-default text-foreground mb-3 text-center md:text-left w-full" style={{
                fontSize: "clamp(1.375rem, 2vw, 1.875rem)",
                lineHeight: 1.2,
                letterSpacing: "-0.01em",
                fontWeight: 500
              }}>
                    {value.title}
                  </h3>

                  {}
                  <p className="font-default text-muted-foreground mb-6 text-center md:text-left" style={{
                fontSize: "clamp(0.95rem, 1vw, 1rem)",
                lineHeight: 1.6
              }}>
                    {value.description}
                  </p>

                  {}
                  <ul className="w-full space-y-3 pt-5 border-t border-border">
                    {value.specs.map((spec, j) => <li data-index={j} key={spec} className="flex items-start gap-3">
                        {}
                        <svg aria-hidden="true" viewBox="0 0 12 14" className="mt-1 w-3 h-3.5 flex-shrink-0">
                          <rect x="2" y="5" width="8" height="8" fill="var(--mercury-accent)" opacity="0.4" />
                        </svg>
                        <span className="font-default text-foreground/90" style={{
                    fontSize: "0.9375rem",
                    lineHeight: 1.5
                  }}>
                          {spec}
                        </span>
                      </li>)}
                  </ul>

                  {}
                  <div className="mt-6 flex items-center gap-2 text-silver-leaf">
                    <Icon className="w-4 h-4" strokeWidth={1.75} />
                    <span className="font-mono uppercase" style={{
                  fontSize: "0.6875rem",
                  letterSpacing: "0.18em"
                }}>
                      Protocol 0{i + 1}
                    </span>
                  </div>
                </CardContent>
              </Card>;
        })}
        </div>
      </div>

      {}
      <svg aria-hidden="true" viewBox="0 0 1440 24" preserveAspectRatio="none" className="absolute bottom-0 left-0 w-full h-6 rotate-180 text-accent/30">
        <path d="M0,12 C80,4 160,20 240,10 C320,2 400,18 480,12 C560,6 640,22 720,14 C800,6 880,20 960,10 C1040,2 1120,18 1200,12 C1280,6 1360,22 1440,14 L1440,0 L0,0 Z" fill="currentColor" />
      </svg>
    </section>;
}